import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { batchThumbs } from "../lib/api";

const CARDS = [
  {
    key: "enterprise",
    label: "ENTERPRISE",
    bgQuery: "modern corporate architecture glass",
    desc: "We provide customized B2B infrastructure and high-res downloads to bring your global projects to life.",
    cta: "Let's collaborate",
    onCta: (nav) => {
      // Scroll to landing pre-registration form (route stays on dashboard; anchor is on "/").
      toast.success("Enterprise team will reach out", { description: "Add your details on the pre-registration form so we can get in touch." });
    },
  },
  {
    key: "livenews",
    label: "LIVE NEWS",
    bgQuery: "photojournalist press camera event",
    desc: "Direct-to-source global news and event coverage. Deliver high-resolution assets within minutes of capture.",
    cta: "Explore News",
    to: "/dashboard/search?q=breaking%20news%20editorial",
  },
];

export default function EnterpriseNews() {
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    let cancelled = false;
    batchThumbs(CARDS.map((c) => c.bgQuery))
      .then((map) => { if (!cancelled) setThumbs(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="enterprise-news"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-20"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {CARDS.map((c) => {
          const thumb = thumbs[c.bgQuery];
          const src = thumb?.src?.large || thumb?.src?.medium;
          return (
            <article
              key={c.key}
              data-testid={`en-card-${c.key}`}
              className="group bg-white shadow-md hover:shadow-xl transition-shadow duration-500 overflow-hidden"
            >
              {/* 4:3 image */}
              <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 3", backgroundColor: thumb?.avg_color || "#111" }}>
                {src && (
                  <img
                    src={src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-transparent" />
                <div className="absolute top-5 left-5 md:top-6 md:left-7">
                  <span className="font-sans font-bold uppercase text-ivory text-3xl md:text-4xl lg:text-5xl tracking-tight leading-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]">
                    {c.label}
                  </span>
                </div>
              </div>

              {/* Text block */}
              <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
                <p className="text-[15px] md:text-base leading-relaxed text-muted-foreground max-w-lg">{c.desc}</p>
                {c.to ? (
                  <Link
                    to={c.to}
                    data-testid={`en-cta-${c.key}`}
                    className="group/btn shrink-0 inline-flex items-center gap-2 self-start md:self-auto px-5 py-2.5 rounded-full bg-gold text-ivory hover:bg-gold/90 text-sm tracking-wide transition-colors"
                  >
                    {c.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => c.onCta()}
                    data-testid={`en-cta-${c.key}`}
                    className="group/btn shrink-0 inline-flex items-center gap-2 self-start md:self-auto px-5 py-2.5 rounded-full bg-gold text-ivory hover:bg-gold/90 text-sm tracking-wide transition-colors"
                  >
                    {c.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
